import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  ChangeDetectorRef,
} from '@angular/core';
import { ThirdPartyService } from 'app/third-party/third-party.service';
import { ToastrService } from 'ngx-toastr';
import { GetEntityQuery, EntityTypeEnum, ScoresInput, GetRoleQuery, APIService, User } from 'app/API.service';
import { EntityService } from 'app/shared/entity.service';
import { UsersSettingsService } from 'app/users-settings/users-settings.service';
import { UserPermissionEnum } from 'app/shared/enums/users-setting.enum';
import { FormBuilder } from '@angular/forms';
import { UtilsService } from 'app/shared/utils.service';
import { AppLevelRoleEnum, AppLevelRoleEnumBnB } from 'app/shared/enums/appLevelRoles.enum';
import { UserService } from 'app/shared/user.service';
import { UsersSettingConstant } from 'app/users-settings/users-settings.constant';
import { CUSTOMAPIService } from 'app/custom-api.service';

export interface TableBodyItem {
  value: string;
  class: string;
  name: string;
}

@Component({
  selector: 'cygov-user-item',
  templateUrl: './user-item.component.html',
  styleUrls: ['./user-item.component.scss'],
  standalone: false,
})
export class UserItemComponent implements OnInit, OnChanges, AfterViewChecked {
  @Input() item: any;
  @Input() isEditable: boolean = true;
  @Input() isVendorUser: boolean;
  @Input() vendorEntities: any[] = [];
  @Input() rolesList: GetRoleQuery[] = [];
  @Input() childEntities: GetEntityQuery[];
  @Input() rootEntity: GetEntityQuery;
  @Input() currentUser: User;
  @Input() openUpwards: boolean = false; // use this to open the last dropdowns to upward direction
  @Output() userUpdated = new EventEmitter<boolean>(); // emit when any user item is changed
  isEditMode: boolean;
  private originalPrivileges: string[] | null = null;
  expanded = false;
  collectionHeight = 20;
  toShowAccess: boolean = false;
  isUpdatingUser = false;
  entityTypeEnum = EntityTypeEnum;

  showColumnTooltip: boolean = false;
  permission: UserPermissionEnum = UserPermissionEnum.EDIT;
  roleOptions: any[] = [];
  permissionOptions: string[] = [...Object.values(UserPermissionEnum)];
  userForm: any; // Reactive form for each item
  selectedChildEntities: GetEntityQuery[] | string[] = [];
  currentUserRole: GetRoleQuery;
  isParticipant = false;
  tmpSelectedEntities: string[] | string = null;
  viewOnlyNotAllowed: boolean = false;
  tmpRoleList: GetRoleQuery[] = [];
  subEntityNames: any[] = [];
  isGlobalUserSetting: boolean;
  globalRootEntity: any;
  globalChildEntities: any[] = [];
  isBnB: boolean = UtilsService.isBnB;
  isBeecher: boolean = UtilsService.isBnBCyberSite;
  isMidMarket: boolean = UtilsService.isMidMarket;
  accessColName: string;
  artifactsColValue: string = '';
  artifactSubEntityObjects: any[] = [];
  toShowArtifactsDropdown: boolean = false;
  isSuperEntityLeaderUser: boolean = false;
  rootEntityOptions: GetEntityQuery[] = [];
  assignedRootEntityIds: string[] = [];
  unmanagedRootEntityIds: string[] = [];
  artifactLockedIds: string[] = [];
  artifactPreSelectedIds: string[] = [];
  isEntityAdmin: boolean;
  rootEntities: any[];
  devOrRegName: any;
  firstTime: boolean = true;
  lastUserActivity = null;
  isParticipantUser: boolean = false;
  artifactsExpanded: boolean = false;
  rowExpanded: boolean = false;
  isArtifactsTruncated: boolean = false;
  @ViewChild('artifactsFirstLine') artifactsFirstLineRef: ElementRef<HTMLElement>;
  constructor(
    private entityService: EntityService,
    private toastrService: ToastrService,
    private usersSettingsService: UsersSettingsService,
    private formBuilderService: FormBuilder,
    private userService: UserService,
    private api: APIService,
    private cdr: ChangeDetectorRef,
    private customAPI: CUSTOMAPIService
  ) {}

  async toggleApiAccess(value: boolean): Promise<void> {
    const user = this.item.user as any;
    try {
      await this.customAPI.updateApiAccess(user.id, value);
      user.apiAccess = value;
      this.item = { ...this.item, user: { ...user } };
      this.userUpdated.emit(true);
      this.toastrService.success(`API access ${value ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      this.toastrService.error('Failed to update API access');
    }
  }

  removeRegulatoryWatchPrivilege(): void {
    const user = this.item.user as any;
    // Snapshot original so we can revert if the user cancels without saving
    if (this.originalPrivileges === null) {
      this.originalPrivileges = [...(user.privileges ?? [])];
    }
    user.privileges = (user.privileges ?? []).filter((p: string) => p !== 'regulatory-watch-admin');
    const formValue = { ...this.userForm.value, privileges: user.privileges };
    this.usersSettingsService.addUserToUpdateList(formValue, this.item.isSelected);
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    if (this.artifactsFirstLineRef) {
      const el = this.artifactsFirstLineRef.nativeElement;
      const truncated = el.scrollWidth > el.clientWidth;
      if (truncated !== this.isArtifactsTruncated) {
        this.isArtifactsTruncated = truncated;
        this.cdr.detectChanges();
      }
    }
  }

  ngOnChanges(changes): void {
    if (changes && changes.rolesList) {
      this.roleOptions = this.rolesList.map(role => role.name);
    }

    // Re-resolve artifactSubEntities names when row/entity inputs refresh asynchronously.
    if (changes?.item || changes?.childEntities) {
      if (this.userForm && this.item?.user) {
        this.userForm.patchValue(
          {
            artifactSubEntities: this.item.user.artifactSubEntities ? this.item.user.artifactSubEntities : [],
          },
          { emitEvent: false }
        );
      }

      if (this.item?.user) {
        this.getArtifactsColumnValue();
      }
    }
  }

  async ngOnInit(): Promise<void> {
    try {
      this.isGlobalUserSetting = this.usersSettingsService.isGlobalUserSettings();
      this.currentUserRole = this.userService.getCurrentRole();
      this.isParticipantUser = this.item?.user?.role?.name?.toLowerCase() === AppLevelRoleEnum.PARTICIPANT;
      this.roleOptions = this.rolesList.length ? this.rolesList.map(role => role.name) : [];
      this.usersSettingsService.getIsEditMode().subscribe(res => {
        this.isEditMode = res;
        if (!res) {
          // Edit mode closed (either save or cancel) — clear the snapshot
          this.originalPrivileges = null;
        }
        if (this.userForm) {
          this.isSubEntityLeader(this.item?.user?.role?.id);
          this.getAccessColumNameValue(this.item?.user?.role?.name);
        }
      });
      this.usersSettingsService.onEditModeCancelled().subscribe(() => {
        if (this.originalPrivileges !== null) {
          // Edit mode cancelled — revert locally removed privilege
          const user = this.item.user as any;
          user.privileges = [...this.originalPrivileges];
          this.originalPrivileges = null;
          this.cdr.detectChanges();
        }
      });
      this.permission = this.item.user.isViewOnly ? UserPermissionEnum.VIEW_ONLY : UserPermissionEnum.EDIT;
      if (
        this.item?.role?.name?.toLowerCase() === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN ||
        this.item?.role?.name?.toLowerCase() === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase()
      ) {
        this.tmpSelectedEntities = this.item?.user?.entityIds?.length ? this.item.user.entityIds[0] : null;
      } else {
        this.tmpSelectedEntities = this.item?.user?.entityIds?.length ? this.item.user.entityIds : null;
      }
      this.tmpRoleList = [...this.rolesList];
      const removeVendorRole = this.rolesList.findIndex(role => role.name === AppLevelRoleEnum.VENDOR);
      if (removeVendorRole > -1) {
        this.tmpRoleList.splice(removeVendorRole, 1);
      }
      this.tmpRoleList = this.filterLowerRoles(this.tmpRoleList);
      if (this.isGlobalUserSetting) {
        this.tmpRoleList = this.removeLowerRoles();
        if (
          this.item?.role?.name?.toLowerCase() === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN ||
          this.item?.role?.name?.toLowerCase() === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase()
        ) {
          this.rootEntities = this.usersSettingsService.getGlobalRootEntities();
          this.isEntityAdmin = true;
        }
      }
      // filtering roles for edit mode on basis of ranking

      // it will be used later
      // this.selectedChildEntities = this.item.user.entityIds ? this.item.user.entityIds : null;
      this.initForm();
      // if name is changed, select checkbox
      this.userForm.valueChanges.subscribe(() => {
        this.userUpdated.emit(true);
      });

      if (this.isGlobalUserSetting) {
        let entityId;
        let entityType;
        const roleName = this.item?.user?.role?.name?.toLowerCase();
        if (
          roleName === AppLevelRoleEnum.ENTITY_LEADER ||
          roleName === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN ||
          roleName === AppLevelRoleEnum.SUBENTITY_LEADER ||
          roleName === AppLevelRoleEnum.PARTICIPANT
        ) {
          entityId = this.item.user.entityIds && this.item.user.entityIds.length ? this.item.user.entityIds[0] : null;
          entityType =
            roleName === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase() ||
            roleName === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN.toLowerCase()
              ? EntityTypeEnum.ROOT_ENTITY
              : EntityTypeEnum.CHILD_ENTITY;
        } else if (this.item.user.role.defaultOrEntityId !== 'default') {
          entityId =
            this.item.user.role.entityIds && this.item.user.role.entityIds.length
              ? this.item.user.role.entityIds[0]
              : null;
          entityType = this.item.user.role.isRootEntityAccess
            ? EntityTypeEnum.ROOT_ENTITY
            : EntityTypeEnum.CHILD_ENTITY;
        }
        if (entityId) {
          const globalEntity = await this.usersSettingsService.getRootAndChildEntities(entityId, entityType);
          if (globalEntity) {
            this.globalChildEntities = globalEntity.childEntities ? globalEntity.childEntities : [];
            this.childEntities = this.globalChildEntities;
            this.globalRootEntity = globalEntity.rootEntity ? globalEntity.rootEntity : null;
          }
        }
      }
      this.setChildEntities();

      this.isSubEntityLeader(this.userForm.value.role ? this.userForm.value.role.id : null);
      this.setViewOnlyPermission(this.userForm.value.role ? this.userForm.value.role.id : null);
      this.getAccessColumnValue();
      this.getArtifactsColumnValue();
      this.setLastLogin();
    } catch (e) {
      console.log(e);
    }
  }
  removeLowerRoles(): any {
    return this.rolesList.filter(
      role =>
        role.name !== AppLevelRoleEnum.VENDOR &&
        role.name !== AppLevelRoleEnum.SUBENTITY_LEADER &&
        role.name !== AppLevelRoleEnumBnB.PARTICIPANT &&
        (role.isRootEntityAccess || role.defaultOrEntityId === UsersSettingConstant.default)
    );
  }

  /**
   * initializing the form.
   */
  initForm(): void {
    this.userForm = this.formBuilderService.group({
      id: [this.item.user.id],
      name: [this.item.user.name ? this.item.user.name : null],
      role: [this.item.user.role ? this.item.user.role : null],
      phone: [this.item.user.phone ? this.item.user.phone : null],
      permission: [this.item.user.isViewOnly ? UserPermissionEnum.VIEW_ONLY : UserPermissionEnum.EDIT],
      entityIds: [this.item.user.entityIds ? this.item.user.entityIds : null],
      artifactSubEntities: [this.item.user.artifactSubEntities ? this.item.user.artifactSubEntities : []],
      entityLayerIds: [this.item.user.entityLayerIds ? this.item.user.entityLayerIds : null],
    });
  }

  onupdate(): void {
    if (!this.checkValidation()) {
      return;
    }
    if (this.changesFound()) {
      this.usersSettingsService.addUserToUpdateList(this.userForm.value, this.item.isSelected);
    } else {
      this.usersSettingsService.removeUserToUpdateList(this.userForm.value);
    }
  }

  setChildEntities(): void {
    if (this.item && this.item.user && this.childEntities && this.childEntities.length) {
      this.selectedChildEntities = this.childEntities.filter(entity =>
        this.userForm?.value?.entityIds?.includes(entity.id) ? entity : null
      );
      this.selectedChildEntities = this.selectedChildEntities.map(entity => entity.name);
    }
  }

  calculateScore(user: GetEntityQuery): ScoresInput {
    return user.scores;
  }

  getImpactText(impact: number): string {
    return ThirdPartyService.getImpactText(impact);
  }

  // this function is not in use
  updateUser(): void {
    this.isUpdatingUser = true;
    this.toastrService.info(`  Updating User: ${this.item.user.name}`);
    try {
      // const userToUpdate = JSON.parse(
      //   JSON.stringify(this.item.user, (k, v) =>
      //     k === '__typename' || k === 'logoUrl' || k === 'isSelected' || k === 'activeAssessment'
      //       ? undefined
      //       : v
      //   )
      // );
      // this.user = await this.entityService.updateEntity(userToUpdate);
      this.isUpdatingUser = false;
      this.toastrService.success(`${this.item.user.name} Updated successfully`);
    } catch (e) {
      this.isUpdatingUser = false;
      this.toastrService.error(`Failed to update ${this.item.user.name}`);
    }
  }

  toggleExpand(event: Event) {
    if (this.item && this.item.access === 'All') {
      return;
    }
    if (
      this.item &&
      (!this.item.entities || this.item.entities.length === 0) &&
      (!this.subEntityNames || this.subEntityNames.length === 0)
    ) {
      return;
    }
    UtilsService.stopBubbling(event);
    this.expanded = !this.expanded;
  }

  toggleArtifactsExpand(event: Event): void {
    UtilsService.stopBubbling(event);
    this.artifactsExpanded = !this.artifactsExpanded;
  }

  toggleRowExpand(event: Event): void {
    UtilsService.stopBubbling(event);
    this.rowExpanded = !this.rowExpanded;
    this.expanded = this.rowExpanded;
    this.artifactsExpanded = this.rowExpanded;
  }

  hidePhoneNumber(phone: string) {
    const reverse = s => s.split('').reverse().join('');
    const replaceRange = (s, start, end, substitute) => s.substring(0, start) + substitute + s.substring(end);
    const reversedPhone = reverse(phone);
    const reversedPhoneCensored = replaceRange(reversedPhone, 2, 8, 'XXXXXXX');
    return reverse(reversedPhoneCensored);
  }
  selectValueChanged(event: any): void {
    this.userForm.value.role = event;
  }

  checkIfEllipsis(element: any): void {
    this.showColumnTooltip = element.scrollWidth > element.clientWidth;
  }

  /**
   * Check either item value is changed or not
   * @returns
   */
  changesFound(): boolean {
    if (
      this.item &&
      this.userForm &&
      (this.item?.user?.name !== this.userForm.value?.name ||
        this.item?.user?.role !== this.userForm.value?.role ||
        this.item?.user?.permission !== this.userForm.value?.permission ||
        JSON.stringify(this.item?.user?.artifactSubEntities || []) !==
          JSON.stringify(this.userForm?.value?.artifactSubEntities || []) ||
        (JSON.stringify(this.item?.user?.entityIds) !== JSON.stringify(this.userForm?.value?.entityIds) &&
          this.toShowAccess))
    ) {
      this.setChildEntities();
      return true;
    }
    return false;
  }

  /**
   * check the Form validations
   * @returns
   */
  checkValidation(): boolean {
    if (!UtilsService.isDefined(this.userForm.value.name)) {
      this.toastrService.error('Name can not be empty');
      return false;
    }
    return true;
  }

  /**
   * check the form subEntity Leader
   * @returns
   */
  isSubEntityLeader(roleId: string | null): void {
    const prevItemRole = this.item.user?.role?.name?.toLowerCase();
    if (roleId) {
      this.toShowAccess = this.rolesList.some(
        role =>
          (role.name.toLowerCase() === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase() ||
            role.name.toLowerCase() === AppLevelRoleEnum.PARTICIPANT.toLowerCase() ||
            role.name.toLowerCase() === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN.toLowerCase()) &&
          role.id === roleId
      );
      this.isParticipant = this.rolesList.some(
        role => role.name.toLowerCase() === AppLevelRoleEnum.PARTICIPANT.toLowerCase() && role.id === roleId
      );
      // show Access Dropdown only in case of GlobalUserSetting for entityLeader
      if (this.isGlobalUserSetting) {
        this.toShowAccess = this.rolesList.some(
          role => role.name.toLowerCase() === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase() && role.id === roleId
        );
      }

      this.isSuperEntityLeaderUser = this.rolesList.some(
        role => role.name.toLowerCase() === AppLevelRoleEnum.SUPER_ENTITY_LEADER.toLowerCase() && role.id === roleId
      );
      if (this.isSuperEntityLeaderUser) {
        void this.loadRootEntityOptions();
      }

      this.isParticipantUser = this.isParticipant;
      this.toShowArtifactsDropdown = this.rolesList.some(
        role =>
          (role.name.toLowerCase() === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase() ||
            role.name.toLowerCase() === AppLevelRoleEnum.PARTICIPANT.toLowerCase()) &&
          role.id === roleId
      );

      // If new role is subEntityLeader and previous is entityLeader then empty the EntityIds Arr.
      if (this.toShowAccess && prevItemRole === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase()) {
        this.userForm.value.entityIds = [];
        // this.tmpSelectedEntities = [];
        if (this.isGlobalUserSetting && !this.firstTime) {
          this.isEntityAdmin = false;
        }
      } else if (!this.toShowAccess && prevItemRole === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase()) {
        // If entityLeader and previous user is subEntityLeader then empty the EntityIds Arr.
        this.userForm.value.entityIds = null;
      } else if (
        (this.toShowAccess && prevItemRole === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase()) ||
        prevItemRole === AppLevelRoleEnum.PARTICIPANT.toLowerCase()
      ) {
        this.userForm.value.entityIds = this.item?.user?.entityIds;
        if (this.isGlobalUserSetting && !this.firstTime) {
          this.isEntityAdmin = true;
        }
      }
    }
  }

  /**
   * checks if the new passed role is true or not with the current user item.
   * @param role
   */
  isPreviousRole(role: AppLevelRoleEnum): boolean {
    return this.item?.user?.role?.name.toLowerCase() === role.toLowerCase();
  }

  updatePermission(val, edit = false): void {
    this.permission = val;
    this.userForm.value.permission = val;
    const userPermission = this.item.user?.isViewOnly ? UserPermissionEnum.VIEW_ONLY : UserPermissionEnum.EDIT;
    if (edit) {
      if (userPermission !== this.userForm.value.permission) {
        this.userUpdated.emit(true);
      } else {
        this.userUpdated.emit(false);
      }
    }
  }

  /** True when the Access column should render the Super Entity Leader root entity multi-select. */
  get showRootEntitySelect(): boolean {
    return this.isEditMode && this.isEditable && this.isSuperEntityLeaderUser && !!this.rootEntityOptions.length;
  }

  /**
   * Root entities a Super Entity Leader row may be assigned. The viewer's scope is applied inside
   * UsersSettingsService.getRootEntitiesForAccess(), which caches one query for every row.
   */
  private async loadRootEntityOptions(): Promise<void> {
    if (this.rootEntityOptions.length) {
      return;
    }
    this.rootEntityOptions = await this.usersSettingsService.getRootEntitiesForAccess();
  }

  /**
   * Access column change for a Super Entity Leader row. Mirrors updateSubEntities(), except the ids are
   * ROOT entity ids, so none of the child-entity or artifact syncing applies.
   * @param event entities emitted by cygov-custom-drop-down
   */
  updateAssignedRootEntities(event: any[]): void {
    const selectedEntityIds = (event || []).map(entity => entity.id);
    const newEntityIds = [...selectedEntityIds, ...this.unmanagedRootEntityIds];
    const isChanged = !this.areEntityIdSetsEqual(this.item.user?.entityIds || [], newEntityIds);
    this.assignedRootEntityIds = selectedEntityIds;
    this.subEntityNames = (event || []).map(entity => entity.name);
    this.userForm.patchValue({ entityIds: newEntityIds }, { emitEvent: false });
    this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';
    if (isChanged) {
      this.item.user.entityIds = [...newEntityIds];
      this.usersSettingsService.addUserToUpdateList({ ...this.userForm.value, entityIds: newEntityIds }, true);
    }
    this.userUpdated.emit(isChanged);
  }

  updateSubEntities(event): void {
    const newEntities = event.map(entity => entity.id);
    this.tmpSelectedEntities = newEntities;
    this.userForm.patchValue({ entityIds: newEntities }, { emitEvent: false });
    this.subEntityNames = event;

    const existingEntities = this.item.user?.entityIds || [];
    const isChanged = !this.areEntityIdSetsEqual(existingEntities, newEntities);

    if (isChanged) {
      this.item.user.entityIds = [...newEntities];
    }
    this.userUpdated.emit(isChanged);

    this.setChildEntities();
    this.getSubEntityNames(this.item.user.entityIds);
    this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';

    // Sync Access selections into Artifacts for sub-entity leader
    const roleName = this.item?.user?.role?.name?.toLowerCase() ?? '';
    if (roleName === AppLevelRoleEnum.SUBENTITY_LEADER) {
      this.artifactLockedIds = [...newEntities];
      // Merge: keep any extra artifact selections already chosen by the user
      const currentArtifacts = this.item.user?.artifactSubEntities ?? [];
      const extra = currentArtifacts.filter(id => !newEntities.includes(id));
      const merged = [...newEntities, ...extra];
      this.item.user.artifactSubEntities = merged;
      this.userForm.patchValue({ artifactSubEntities: merged }, { emitEvent: false });
      this.artifactPreSelectedIds = [...merged];
    }
  }

  /**
   * Function to update Entity Ids for prticipant on user selection
   * @param event - Currently Selected entities
   */
  updateSubEntitiesParticipant(event: any): void {
    const newEntities = event.map(entity => entity.id);
    this.tmpSelectedEntities = newEntities;
    this.userForm.patchValue({ entityIds: newEntities }, { emitEvent: false });
    this.subEntityNames = event;

    const existingEntities = this.item.user?.entityIds || [];
    const isChanged = !this.areEntityIdSetsEqual(existingEntities, newEntities);

    if (isChanged) {
      this.item.user.entityIds = [...newEntities];
    }
    this.userUpdated.emit(isChanged);

    this.setChildEntities();
    this.getSubEntityNames(this.item.user.entityIds);
  }

  /**
   * if role of user is updated to ADMIN or PARTICIPANT then hide view only option from permission list
   * @param roleId is type of string
   */
  setViewOnlyPermission(roleId: string | null): void {
    // console.clear();
    /* Commenting the code below because we need to display the original permissions granted to users during creation.
   However, this code is converting permissions for "admins and participants" from "view only" to "edit". */
    // if (roleId) {
    //   this.viewOnlyNotAllowed = this.rolesList.some(
    //     role =>
    //       (role.name.toLowerCase() === AppLevelRoleEnum.ADMIN.toLowerCase() ||
    //         role.name.toLowerCase() === AppLevelRoleEnum.PARTICIPANT.toLowerCase()) &&
    //       role.id === roleId
    //   );
    //   if (this.viewOnlyNotAllowed) {
    //     this.updatePermission(UserPermissionEnum.EDIT);
    //   }
    // }
  }

  /**
   * chnage the accessColName value according to the role.
   * @param role user role name in string
   * @returns
   */
  getAccessColumNameValue(role: string = '') {
    const roleName = role;
    if (
      roleName.toLowerCase() === AppLevelRoleEnum.ADMIN.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnum.MSSP.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.ENTERPRISE.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.USER_ADMINISTRATOR.toLowerCase()
    ) {
      this.accessColName = 'All';
    } else if (
      roleName.toLowerCase() === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN.toLowerCase()
    ) {
      // This line is only written for Global setting demo later on we will change it.
      let entity;
      if (this.isGlobalUserSetting) {
        entity = this.globalRootEntity ? this.globalRootEntity : '-';
      } else {
        entity = this.rootEntity ? this.rootEntity : '-';
      }
      this.accessColName = entity && entity.name ? entity.name : '-';
    } else {
      this.accessColName = '';
    }
  }

  async getAccessColumnValue(): Promise<void> {
    const roleName =
      this.item && this.item.user && this.item.user.role && this.item.user.role.name ? this.item.user.role.name : '';
    if (
      roleName.toLowerCase() === AppLevelRoleEnum.ADMIN.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnum.MSSP.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.ENTERPRISE ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.USER_ADMINISTRATOR
    ) {
      this.accessColName = 'All';
    } else if (
      roleName.toLowerCase() === AppLevelRoleEnumBnB.DIVISION_LEADER ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.REGION_LEADER
    ) {
      if (this.item?.user?.entityLayerIds && this.item?.user?.entityLayerIds.length) {
        this.accessColName = await this.entityService.getDivRegName(this.item?.user?.entityLayerIds[0]);
      }
    } else if (roleName.toLowerCase() === AppLevelRoleEnum.SUPER_ENTITY_LEADER.toLowerCase()) {
      await this.loadRootEntityOptions();
      const manageableIds = new Set(this.rootEntityOptions.map(entity => entity.id));
      const userEntityIds = this.item.user.entityIds || [];
      this.assignedRootEntityIds = userEntityIds.filter(id => manageableIds.has(id));
      this.unmanagedRootEntityIds = userEntityIds.filter(id => !manageableIds.has(id));
      this.subEntityNames = this.rootEntityOptions
        .filter(entity => this.assignedRootEntityIds.includes(entity.id))
        .map(entity => entity.name);
      this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';
    } else if (
      roleName.toLowerCase() === AppLevelRoleEnum.ENTITY_LEADER.toLowerCase() ||
      roleName.toLowerCase() === AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN.toLowerCase() ||
      (this.item && this.item.user && this.item.user.role && this.item.user.role.isRootEntityAccess)
    ) {
      // This line is only written for Global setting demo later on we will change it.
      let entity;
      if (this.isGlobalUserSetting) {
        entity = this.globalRootEntity ? this.globalRootEntity : '-';
      } else {
        entity = this.rootEntity ? this.rootEntity : '-';
      }
      this.accessColName = entity && entity.name ? entity.name : '-';
    } else if (roleName.toLowerCase() === AppLevelRoleEnum.VENDOR.toLowerCase()) {
      // ( Note ) : No need to call API for each item to show the data.
      //            it increase the number of calls to get same entity which already got.
      if (this.item && this.item.user && this.item.user.entityIds && this.item.user.entityIds.length) {
        // According to new requirement a vendor user can has multiple vendor entities assigned
        this.subEntityNames = []; // Contains assigned vendor entities names
        this.item.user.entityIds.map(id => {
          if (this.vendorEntities[id]) {
            this.subEntityNames.push(this.vendorEntities[id]?.name);
          }
        });
        this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';
      }
    } else if (roleName.toLowerCase() === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase()) {
      this.getSubEntityNames(this.item.user.entityIds, true);
      this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';
    } else if (roleName.toLowerCase() === AppLevelRoleEnum.PARTICIPANT.toLowerCase()) {
      // If BNB then get the subEntities list from the user entity Ids.
      if (UtilsService.isBnB || UtilsService.isBnBCyberSite) {
        const subEntityList = await this.userService.getEntitiesListbyEntityIds(this.item.user.entityIds);
        if (subEntityList && subEntityList.length) {
          this.subEntityNames = subEntityList.map(subEnt => subEnt.name);
        }
      } else {
        this.getSubEntityNames(this.item.user.entityIds, true);
      }
    } else if (
      this.item &&
      this.item.user &&
      this.item.user.role &&
      this.item.user.role.entityIds &&
      this.item.user.role.entityIds.length &&
      this.item.user.role.defaultOrEntityAccess !== 'default'
    ) {
      this.getSubEntityNames(this.item.user.role.entityIds);
    }
  }

  getSubEntityNames(givenEntities: any[] = [], showOnlyAssigned = false): void {
    givenEntities = givenEntities ? givenEntities : [];
    const entities = [];
    let childEntities = this.isGlobalUserSetting && this.isBnB ? this.globalChildEntities : this.childEntities;
    if (!childEntities) {
      return;
    }
    const grantAccess =
      this.currentUserRole &&
      this.currentUserRole?.name.toLowerCase() === AppLevelRoleEnum.SUBENTITY_LEADER.toLowerCase();

    if (showOnlyAssigned && grantAccess) {
      this.item.access = this.subEntityNames.length > 0 ? this.subEntityNames : '';
      childEntities = childEntities.filter(entity => this.currentUser.entityIds.includes(entity.id));
      this.childEntities = childEntities;
    }
    givenEntities.map(entityId => {
      const childEntity = childEntities.find(e => e.id === entityId);
      if (childEntity) {
        entities.push(childEntity.name);
      }
    });
    this.subEntityNames = entities;
  }

  compairRole(role: any, selectedRole: any) {
    return role && selectedRole && role.id === selectedRole.id;
  }

  async selectRole(event: any): Promise<void> {
    this.userForm.value.role = event;
    let roleName = this.rolesList.filter(role => role.id === this.userForm?.value?.role)[0]?.name;
    this.firstTime = false;
    // if role is changed, select checkbox
    this.isSubEntityLeader(this.userForm.value.role);
    if (this.userForm.value.role === this.item.user.role.id) {
      this.userUpdated.emit(false);
    } else {
      this.userUpdated.emit(true);
    }
    this.setViewOnlyPermission(this.userForm.value.role);
    this.getAccessColumNameValue(roleName);
    const selectedRole = this.rolesList.find(role => role.id === event);
    if (selectedRole) {
      roleName = selectedRole.name;
      if (
        [AppLevelRoleEnum.ADMIN, AppLevelRoleEnum.MSSP].some(role => role === roleName) ||
        (selectedRole.entityIds && selectedRole.isRootEntityAccess)
      ) {
        this.item.access = 'All';
        this.subEntityNames = [];
      } else if (roleName === AppLevelRoleEnum.ENTITY_LEADER) {
        // Entity Leader only has access to root entity, not sub-entities
        let entity;
        if (this.isGlobalUserSetting) {
          entity = this.globalRootEntity ? this.globalRootEntity : null;
        } else {
          entity = this.rootEntity ? this.rootEntity : null;
        }
        this.item.access = entity && entity.name ? entity.name : '';
        this.subEntityNames = [];
      } else {
        await this.getAccessColumnValue();
      }
      this.getArtifactsColumnValue();
    }

    if (this.isBnB) {
      await this.bnbRoleChange(event);
    }
  }
  /**
   * filter those roles which are lower than the current user's role.
   * @param tmpRoleList is type of GetRoleQuery List.
   * @returns roles list
   */
  filterLowerRoles(tmpRoleList: GetRoleQuery[]): GetRoleQuery[] {
    return tmpRoleList.filter(
      role =>
        this.userService.hasPermissionHierarchy(AppLevelRoleEnum.MSSP) ||
        this.userService.hasPermissionHierarchy(AppLevelRoleEnumBnB.USER_ADMINISTRATOR) ||
        this.usersSettingsService.isCurrentRoleManageGivenRole(this.currentUserRole, role)
    );
  }

  async bnbRoleChange(roleId: string): Promise<void> {
    const selectedRole = this.rolesList.find(role => role.id === roleId);
    if (selectedRole) {
      // const selectedRole = this.rolesList[roleIndex];
      switch (selectedRole.name.toLowerCase()) {
        case AppLevelRoleEnumBnB.ENTERPRISE:
          this.userForm.value.entityIds = [];
          this.userForm.value.entityLayerIds = [];
          break;
        case AppLevelRoleEnumBnB.DIVISION_LEADER: {
          const divisionId = await this.entityService.getDivisionRegionId(true, this.rootEntity.id);
          this.userForm.value.entityIds = [];
          this.userForm.value.entityLayerIds = [divisionId];
          break;
        }
        case AppLevelRoleEnumBnB.REGION_LEADER: {
          const regionId = await this.entityService.getDivisionRegionId(true, this.rootEntity.id);
          this.userForm.value.entityIds = [];
          this.userForm.value.entityLayerIds = [regionId];
          break;
        }
        case AppLevelRoleEnumBnB.BUSINESS_ENTITY_ADMIN:
          // this.userForm.value.entityIds = this.tmpSelectedEntities;
          this.rootEntities = this.usersSettingsService.getGlobalRootEntities();
          this.userForm.value.entityLayerIds = [];
          if (this.isGlobalUserSetting) {
            this.isEntityAdmin = true;
          }
          break;
        default:
          this.userForm.value.entityLayerIds = [];
      }
    }
  }

  userRoleForEnterprise(item: any): string {
    if (item && item.user && item.user.role) {
      return this.isBnB ? item?.user?.role?.secondaryName : item?.user?.role?.name;
    } else {
      return 'Select Role';
    }
  }

  setLastLogin(): void {
    const timeStamps = [];
    if (this.item?.user?.lastLogin) {
      timeStamps.push(this.item.user.lastLogin);
    }
    if (this.item?.user?.modifiedAt) {
      timeStamps.push(this.item.user.modifiedAt);
    }
    if (this.item?.user?.updatedAt) {
      const dateTime = new Date(this.item.user.updatedAt);
      timeStamps.push(dateTime.getTime());
    }
    if (timeStamps.length) {
      timeStamps.sort();
      this.lastUserActivity = timeStamps[timeStamps.length - 1];
    }
  }

  getArtifactsColumnValue(): void {
    const roleName = this.item?.user?.role?.name?.toLowerCase() ?? '';
    const adminRoles = [
      AppLevelRoleEnum.ADMIN,
      AppLevelRoleEnum.MSSP,
      AppLevelRoleEnum.SUPER_ENTITY_LEADER,
      AppLevelRoleEnum.ENTITY_LEADER,
    ];
    if (adminRoles.includes(roleName as AppLevelRoleEnum)) {
      this.artifactsColValue = 'ALL';
      this.toShowArtifactsDropdown = false;
      return;
    }
    const subEntityRoles = [AppLevelRoleEnum.SUBENTITY_LEADER, AppLevelRoleEnum.PARTICIPANT];
    if (subEntityRoles.includes(roleName as AppLevelRoleEnum)) {
      this.toShowArtifactsDropdown = true;
      const ids: string[] = this.item?.user?.artifactSubEntities ?? [];
      const entities = this.isGlobalUserSetting ? this.globalChildEntities : (this.childEntities ?? []);
      this.artifactSubEntityObjects = ids.map(id => entities?.find((e: any) => e.id === id)).filter(Boolean);
      const names = this.artifactSubEntityObjects.map((e: any) => e.name);
      this.artifactsColValue = names.length ? names.join(', ') : '-';
      this.artifactLockedIds = roleName === AppLevelRoleEnum.SUBENTITY_LEADER ? (this.item?.user?.entityIds ?? []) : [];
      this.artifactPreSelectedIds = [...ids];
      return;
    }
    this.artifactLockedIds = [];
    this.artifactPreSelectedIds = [];
    this.toShowArtifactsDropdown = false;
    this.artifactsColValue = '';
  }

  updateArtifactSubEntities(event: any[]): void {
    const newIds = event.map((entity: any) => entity.id);
    this.artifactSubEntityObjects = event;
    this.item.user.artifactSubEntities = newIds;
    this.userForm.patchValue({ artifactSubEntities: newIds });
    this.artifactsColValue = event.length ? event.map((e: any) => e.name).join(', ') : '-';
    this.userUpdated.emit(true);
    this.onupdate();
  }

  areArraysEqual(arr1: any, arr2: any): boolean {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) {
      return false;
    }
    // Check if the arrays are of the same length
    if (arr1.length !== arr2.length) {
      return false;
    }

    // Check each item in the arrays for equality
    arr1.forEach((item, index) => {
      if (item !== arr2[index]) {
        return false;
      }
    });

    // If all checks pass, the arrays are the same
    return true;
  }

  private areEntityIdSetsEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const setA = new Set(a);
    return b.every(id => setA.has(id));
  }
}
